import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import type { FitnessParameter, FitnessActivity, FitnessParameterType } from '../types';
import { PERFORMANCE_PARAMETER_NAMES } from '../constants/fitnessParameters';
import { MUSCLE_GROUP_NAMES } from '../constants/muscleGroups';
import {
  calculateFitnessLevel,
  calculateCurrentLevelXp,
  calculateXpToNextLevel,
} from '../utils/fitnessXP';

interface FitnessStore {
  parameters: FitnessParameter[];
  activities: FitnessActivity[];
  /** Cumulative XP from direct Quick-Adds (credits the Personal domain once). */
  directXp: number;
  /**
   * The account `directXp` was loaded for (null = not loaded) — what guards its
   * catch-up when the tab comes back. The parameters load on their own, so the
   * store has no single "initialized for".
   */
  directXpUserId: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  lastSyncTime: number | null;

  initializeParameters: () => Promise<void>;
  loadParameters: () => Promise<void>;
  loadDirectXp: () => Promise<void>;
  loadActivities: (parameterId?: string, limit?: number) => Promise<void>;
  addXP: (parameterName: string, xpAmount: number, activityDescription?: string, creditsDomain?: boolean) => Promise<void>;
  getParameterByName: (name: string) => FitnessParameter | undefined;
  getParametersByType: (type: FitnessParameterType) => FitnessParameter[];
  getTotalFitnessXP: () => number;
  getTotalFitnessLevel: () => number;
  clearStore: () => void;
}

const num = (v: unknown): number => (v != null && Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * `user_preferences.fitness_direct_xp` is a COUNTER. It used to be written as an
 * absolute value, so two devices each granting XP kept only the last grant — a
 * stale tab wrote its old total plus its own XP over everything earned since.
 * Merged as a delta now: the cloud's total plus what THIS tab added since its base.
 */
const directXpSync = createPreferenceSync<number>({
  column: 'fitness_direct_xp',
  rule: (base, local, cloud) => num(cloud) + num(local) - num(base),
  decode: num,
  read: () => useFitnessStore.getState().directXp,
  adopt: (directXp) => useFitnessStore.setState({ directXp }),
  label: 'fitness XP',
  loadedFor: () => useFitnessStore.getState().directXpUserId,
});

/** Await the pending direct-XP write (tests). */
export const flushFitnessDirectXp = (): Promise<void> => directXpSync.flush();

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

export const useFitnessStore = create<FitnessStore>((set, get) => ({
  parameters: [],
  activities: [],
  directXp: 0,
  directXpUserId: null,
  isLoading: false,
  isInitialized: false,
  lastSyncTime: null,

  initializeParameters: async () => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;

    try {
      const { data: existingParams } = await supabase
        .from('fitness_parameters')
        .select('*')
        .eq('user_id', user.id);
      if (at !== epoch) return; // signed out while it read: nothing to create, nothing to show

      if (existingParams && existingParams.length > 0) {
        set({ isInitialized: true });
        return;
      }

      const allParameterNames = [
        ...PERFORMANCE_PARAMETER_NAMES.map(name => ({ name, type: 'performance' as const })),
        ...MUSCLE_GROUP_NAMES.map(name => ({ name, type: 'muscle_group' as const }))
      ];

      const parametersToInsert = allParameterNames.map(param => ({
        user_id: user.id,
        parameter_name: param.name,
        parameter_type: param.type,
        level: 1,
        xp: 0,
        current_level_xp: 0,
        xp_to_next_level: 100
      }));

      const { error } = await supabase
        .from('fitness_parameters')
        .insert(parametersToInsert);

      if (error) throw error;

      await get().loadParameters();
      if (at === epoch) set({ isInitialized: true });
    } catch (error) {
      console.error('Error initializing fitness parameters:', error);
    }
  },

  loadParameters: async () => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== epoch) return;

    set({ isLoading: true });

    try {
      const { data, error } = await supabase
        .from('fitness_parameters')
        .select('*')
        .eq('user_id', user.id)
        .order('parameter_type', { ascending: true })
        .order('parameter_name', { ascending: true });
      if (at !== epoch) return; // signed out while it read

      if (error) throw error;

      const parameters: FitnessParameter[] = (data || []).map(param => ({
        id: param.id,
        userId: param.user_id,
        parameterName: param.parameter_name,
        parameterType: param.parameter_type,
        level: param.level,
        xp: param.xp,
        currentLevelXp: param.current_level_xp,
        xpToNextLevel: param.xp_to_next_level,
        createdAt: param.created_at,
        updatedAt: param.updated_at
      }));

      set({ parameters, isLoading: false, lastSyncTime: Date.now() });
    } catch (error) {
      if (at !== epoch) return; // the next account's load may be running: leave its flag alone
      console.error('Error loading fitness parameters:', error);
      set({ isLoading: false });
    }
  },

  loadDirectXp: async () => {
    const at = epoch;
    const prefs = await fetchUserPreferences();
    if (at !== epoch) return; // signed out while it read: never that account's total, nor its base
    set({
      directXp: directXpSync.load(prefs?.row?.fitness_direct_xp ?? null, prefs?.row?.updated_at),
      // A failed read counts too: its 0 is also its base, so a catch-up takes the
      // cloud's total (plus anything added here) — a counter has nothing to lose.
      directXpUserId: prefs?.userId ?? null,
    });
  },

  loadActivities: async (parameterId?: string, limit = 50) => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;

    try {
      let query = supabase
        .from('fitness_parameter_activities')
        .select('*')
        .eq('user_id', user.id)
        .order('logged_at', { ascending: false })
        .limit(limit);

      if (parameterId) {
        query = query.eq('parameter_id', parameterId);
      }

      const { data, error } = await query;
      if (at !== epoch) return; // signed out while it read

      if (error) throw error;

      const activities: FitnessActivity[] = (data || []).map(activity => ({
        id: activity.id,
        userId: activity.user_id,
        parameterId: activity.parameter_id,
        xpEarned: activity.xp_earned,
        activityDescription: activity.activity_description,
        loggedAt: activity.logged_at,
        createdAt: activity.created_at
      }));

      set({ activities });
    } catch (error) {
      console.error('Error loading fitness activities:', error);
    }
  },

  addXP: async (parameterName: string, xpAmount: number, activityDescription?: string, creditsDomain = false) => {
    const at = epoch;
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;

    const parameter = get().parameters.find(p => p.parameterName === parameterName);
    if (!parameter) return;

    try {
      const newTotalXp = parameter.xp + xpAmount;
      const newLevel = calculateFitnessLevel(newTotalXp);
      const newCurrentLevelXp = calculateCurrentLevelXp(newTotalXp);
      const newXpToNextLevel = calculateXpToNextLevel(newTotalXp);

      const { error: updateError } = await supabase
        .from('fitness_parameters')
        .update({
          xp: newTotalXp,
          level: newLevel,
          current_level_xp: newCurrentLevelXp,
          xp_to_next_level: newXpToNextLevel,
          updated_at: new Date().toISOString()
        })
        .eq('id', parameter.id);

      if (updateError) throw updateError;

      const { error: activityError } = await supabase
        .from('fitness_parameter_activities')
        .insert({
          user_id: user.id,
          parameter_id: parameter.id,
          xp_earned: xpAmount,
          activity_description: activityDescription,
          logged_at: new Date().toISOString()
        });

      if (activityError) throw activityError;

      await get().loadParameters();
      await get().loadActivities(parameter.id, 10);
      // ⚠️ Signed out meanwhile: this account's XP must never be added to the NEXT
      // account's total (and written to its preferences by the counter's sync).
      if (at !== epoch) return;

      // Direct Quick-Adds credit the Personal domain once (mirror paths — DS2
      // attribution + program modules — pass creditsDomain=false; their source
      // action already credited the domain).
      if (creditsDomain) {
        directXpSync.recordBase(get().directXp);
        set({ directXp: get().directXp + xpAmount });
        directXpSync.schedule();
      }

      if (newLevel > parameter.level) {
        console.log(`🎉 Level up! ${parameterName} reached level ${newLevel}!`);
      }
    } catch (error) {
      console.error('Error adding fitness XP:', error);
    }
  },

  getParameterByName: (name: string) => {
    return get().parameters.find(p => p.parameterName === name);
  },

  getParametersByType: (type: FitnessParameterType) => {
    return get().parameters.filter(p => p.parameterType === type);
  },

  getTotalFitnessXP: () => {
    return get().parameters.reduce((total, param) => total + param.xp, 0);
  },

  getTotalFitnessLevel: () => {
    const totalXp = get().getTotalFitnessXP();
    return calculateFitnessLevel(totalXp);
  },

  /** Back to empty — run on sign-out (lib/signOutReset). */
  clearStore: () => {
    epoch += 1;
    directXpSync.reset();
    set({
      parameters: [],
      activities: [],
      directXp: 0,
      directXpUserId: null,
      isLoading: false,
      isInitialized: false,
      lastSyncTime: null
    });
  }
}));

onSignOut(() => useFitnessStore.getState().clearStore());
